<script setup>
/**
 * 计费区严格区分调用失败、供应商成本未知、等待结算、正常零积分、正常扣分和账本异常。
 *
 *   charged                 扣了多少、余额变成多少（billing_ledger 里的那一行）
 *   not_charged_failed_call 调用失败/中止，设计上不计费
 *   record_only             保存成本与策略，但不写扣分流水
 *   not_charged_zero_points 收费公式结果为零，不写扣分流水
 *   settlement_pending      供应商成本已保存，积分终态尚未形成
 *   missing_ledger_row      **真异常**：成功、烧了 token、却没扣钱 —— 要显眼，要能跳到对账页
 *   unpriceable             供应商成本事实没有落库
 *
 * `ledger_mismatch` 与 `settlement_mismatch` 同样是异常；历史模式必须明确说明旧策略参数未知。
 */
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { Badge, Button } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { formatDateTime, formatInt } from '../llm-format.js'

const props = defineProps({
  /** @type {import('vue').PropType<import('../../../api/llm/index.js').CallBilling>} */
  billing: { type: Object, required: true },
  /** 这次调用记在谁头上。账本按用户读 —— 没有用户就没法下钻（直连调用的 `user_id` 是 NULL）。 */
  userId: { type: String, default: '' },
})

const { t } = useI18n()
const router = useRouter()

const anomalous = computed(() => ['missing_ledger_row', 'ledger_mismatch', 'settlement_mismatch'].includes(props.billing.state))
const billingMode = computed(() => props.billing.billing_mode ?? null)
const billingPolicyDetail = computed(() => {
  const snapshot = props.billing.billing_policy_snapshot
  switch (billingMode.value) {
    case null:
      return null
    case 'legacy_points':
      return t('visualGameAdmin.llm.calls.detail.billing.legacyPointsExplanation')
    case 'legacy_record_only':
      return t('visualGameAdmin.llm.calls.detail.billing.legacyRecordOnlyExplanation')
    case 'record_only':
      return t('visualGameAdmin.llm.calls.detail.billing.policy.recordOnly', { reason: snapshot.reason })
    case 'fixed_points':
      return t('visualGameAdmin.llm.calls.detail.billing.policy.fixedPoints', { points: snapshot.points })
    case 'cost_ratio':
      return t('visualGameAdmin.llm.calls.detail.billing.policy.costRatio', { points: snapshot.points_per_yuan })
    default:
      throw new Error(`Unhandled billing mode: ${billingMode.value}`)
  }
})

/** 对账页（S8）按 ref_id 把 llm_call_log 和 billing_ledger 拼起来，找的正是这一类行。 */
function openRecon() {
  router.push({ name: 'llm-reconciliation' })
}

/**
 * 下钻到这个用户的账本，把这一行高亮出来。
 * 「扣了 ¥0.001，余额变成 ¥999.996」—— 之前是多少、后面还扣了什么，只有账本回答得了。
 */
function openLedger() {
  router.push({
    name: 'llm-ledger',
    params: { userId: props.userId },
    query: props.billing.ledger ? { highlight: props.billing.ledger.id } : {},
  })
}
</script>

<template>
  <section
    class="call-billing"
    data-insp-path="apps/visual-game-admin/src/pages/llm/call/CallBilling.vue#billing"
  >
    <header class="call-billing__head">
      <h3>{{ t('visualGameAdmin.llm.calls.detail.billing.title') }}</h3>
      <Badge :variant="anomalous ? 'danger' : billing.state === 'charged' ? 'success' : 'neutral'">
        {{ t(`visualGameAdmin.llm.calls.detail.billing.state.${billing.state}`) }}
      </Badge>
      <Button
        v-if="userId"
        class="call-billing__drill"
        variant="ghost"
        size="sm"
        @click="openLedger"
      >
        {{ t('visualGameAdmin.llm.calls.detail.billing.gotoLedger') }}
      </Button>
    </header>

    <template v-if="billing.state === 'charged'">
      <dl class="call-billing__rows">
        <div>
          <dt>{{ t('visualGameAdmin.llm.calls.detail.billing.charged') }}</dt>
          <dd class="call-billing__amount">
            {{ formatInt(billing.charged_points) }}
          </dd>
        </div>
        <div>
          <dt>{{ t('visualGameAdmin.llm.calls.detail.billing.balanceAfter') }}</dt>
          <dd class="mono">
            {{ formatInt(billing.ledger.balance_after) }}
          </dd>
        </div>
        <div>
          <dt>{{ t('visualGameAdmin.llm.calls.detail.billing.chargedAt') }}</dt>
          <dd class="mono">
            {{ formatDateTime(billing.ledger.created_at) }}
          </dd>
        </div>
        <div>
          <dt>{{ t('visualGameAdmin.llm.calls.detail.billing.ledgerId') }}</dt>
          <dd>
            <button
              class="call-billing__ledger-id"
              type="button"
              @click="openLedger"
            >
              {{ billing.ledger.id }}
            </button>
          </dd>
        </div>
      </dl>
    </template>

    <p
      v-else-if="billing.state === 'not_charged_failed_call'"
      class="call-billing__headline"
    >
      {{ t('visualGameAdmin.llm.calls.detail.billing.notChargedFailedCall') }}
    </p>

    <p
      v-else-if="billing.state === 'record_only' || billing.state === 'not_charged_zero_points' || billing.state === 'settlement_pending'"
      class="call-billing__headline"
    >
      {{ t(`visualGameAdmin.llm.calls.detail.billing.state.${billing.state}`) }}
    </p>

    <template v-else-if="billing.state === 'missing_ledger_row'">
      <p class="call-billing__headline vga-error">
        {{ t('visualGameAdmin.llm.calls.detail.billing.missingLedgerRow') }}
      </p>
      <dl class="call-billing__rows">
        <div>
          <dt>{{ t('visualGameAdmin.llm.calls.detail.billing.expected') }}</dt>
          <dd class="call-billing__amount">{{ formatInt(billing.expected_charged_points) }}</dd>
        </div>
      </dl>
      <div>
        <Button
          variant="danger"
          size="sm"
          @click="openRecon"
        >
          {{ t('visualGameAdmin.llm.calls.detail.billing.gotoRecon') }}
        </Button>
      </div>
    </template>

    <template v-else-if="billing.state === 'ledger_mismatch' || billing.state === 'settlement_mismatch'">
      <p class="call-billing__headline vga-error">
        {{ t(`visualGameAdmin.llm.calls.detail.billing.state.${billing.state}`) }}
      </p>
      <Button variant="danger" size="sm" @click="openRecon">
        {{ t('visualGameAdmin.llm.calls.detail.billing.gotoRecon') }}
      </Button>
    </template>

    <template v-else-if="billing.state === 'unpriceable'">
      <p class="call-billing__headline">
        {{ t('visualGameAdmin.llm.calls.detail.billing.unpriceable') }}
      </p>
      <p class="call-billing__why">
        {{ t(`visualGameAdmin.llm.calls.detail.billing.unpriceableReason.${billing.reason}`) }}
      </p>
    </template>

    <dl v-if="billingMode" class="call-billing__rows">
      <div>
        <dt>{{ t('visualGameAdmin.llm.calls.detail.billing.policyMode') }}</dt>
        <dd>{{ t(`visualGameAdmin.llm.calls.detail.billing.mode.${billingMode}`) }}</dd>
      </div>
      <div>
        <dt>{{ t('visualGameAdmin.llm.calls.detail.billing.policyDetail') }}</dt>
        <dd>{{ billingPolicyDetail }}</dd>
      </div>
    </dl>
  </section>
</template>

<style scoped>
.call-billing {
  display: grid;
  gap: var(--drama-space-md);
  min-width: 0;
}

.call-billing__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--drama-space-md);
}

.call-billing__head h3 {
  margin: 0;
  font-family: var(--drama-font-family-heading);
  font-size: var(--drama-font-size-lg);
  font-weight: var(--drama-font-weight-medium);
}

.call-billing__headline {
  margin: 0;
  color: var(--drama-color-text);
  font-size: var(--drama-font-size-md);
}

.call-billing__why {
  margin: 0;
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-sm);
}

.call-billing__rows {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: var(--drama-space-md);
  margin: 0;
}

.call-billing__rows dt {
  color: var(--drama-color-text-muted);
  font-size: var(--drama-font-size-xs);
}

.call-billing__rows dd {
  margin: var(--drama-space-xs) 0 0;
  font-size: var(--drama-font-size-sm);
}

.call-billing__amount {
  color: var(--drama-color-text);
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-lg);
  font-variant-numeric: tabular-nums;
}

.mono {
  font-family: var(--drama-font-family-mono);
  font-variant-numeric: tabular-nums;
  overflow-wrap: anywhere;
}

.call-billing__drill {
  margin-left: auto;
}

.call-billing__ledger-id {
  padding: 0;
  border: none;
  background: none;
  color: var(--drama-color-text);
  font-family: var(--drama-font-family-mono);
  font-size: var(--drama-font-size-sm);
  text-align: left;
  text-decoration: underline;
  text-underline-offset: 3px;
  overflow-wrap: anywhere;
  cursor: pointer;
}
</style>
